import { FC, useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import {
  CHECK_IF_PRACTICE_TEST_USED_IN_ADDITIONAL_TESTS,
  DELETE_PRACTICE_TEST,
  GET_ONE_PRACTICE_TEST,
  UPDATE_PRACTICE_TEST,
} from 'queries/practiceTest'
import { CreatePracticeTestInput, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { updatePracticeTest, updatePracticeTestVariables } from '__generatedTypes__/updatePracticeTest'
import { findOnePracticeTest, findOnePracticeTestVariables } from '__generatedTypes__/findOnePracticeTest'
import { removePracticeTest, removePracticeTestVariables } from '__generatedTypes__/removePracticeTest'
import {
  checkIfPracticeTestUsedInAdditionalTests,
  checkIfPracticeTestUsedInAdditionalTestsVariables,
} from '__generatedTypes__/checkIfPracticeTestUsedInAdditionalTests'
import { PlatformSlug } from 'constants/consts'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form, PracticeTest } from '../Form'
import { getPTConfig } from '../consts'

export const Edit: FC<{ type?: PracticeTestTypeEnum }> = ({ type = PracticeTestTypeEnum.PracticeTest }) => {
  const { practiceTestId = '', platform } = useParams()
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updatePracticeTest, updatePracticeTestVariables>(UPDATE_PRACTICE_TEST)
  const [remove] = useMutation<removePracticeTest, removePracticeTestVariables>(DELETE_PRACTICE_TEST)
  const { data, loading } = useQuery<findOnePracticeTest, findOnePracticeTestVariables>(GET_ONE_PRACTICE_TEST, {
    variables: {
      params: practiceTestId,
    },
    fetchPolicy: 'network-only',
  })
  const { data: isUsedData, loading: isUsedLoading } = useQuery<
    checkIfPracticeTestUsedInAdditionalTests,
    checkIfPracticeTestUsedInAdditionalTestsVariables
  >(CHECK_IF_PRACTICE_TEST_USED_IN_ADDITIONAL_TESTS, {
    variables: {
      id: practiceTestId,
    },
    fetchPolicy: 'network-only',
  })
  const practiceTest = data?.findOnePracticeTest
  const questions = data?.findQuestionByRelation ?? []
  const subject = practiceTest?.subject ?? null

  const config = useMemo(
    () =>
      getPTConfig(
        type,
        platform as PlatformSlug,
        subject,
        practiceTest?.type === PracticeTestTypeEnum.ReadingQuestionBank
      ),
    [type, subject, practiceTest?.type, platform]
  )

  const handleSubmit = useCallback(
    async (practiceTest: CreatePracticeTestInput) => {
      if (!config) {
        return
      }

      try {
        const { data } = await update({
          variables: {
            params: {
              id: practiceTestId,
              ...practiceTest,
              history: populateHistoryWithUser(practiceTest.history),
              isLive: undefined, // isLive can be changed only on PTs list
            },
          },
        })

        if (data?.updatePracticeTest.id) {
          navigate(config.dashboardLink, undefined, {
            searchParams: new URLSearchParams({
              subjectId: String(subject?.id),
              ...(config.dashboardSearchParams ?? {}),
            }),
          })

          toast.success(`${config.titleSingle} updated successfully`)
        } else {
          toast.error(`Failed to update ${config.titleSingle}`)
        }
      } catch (error) {
        toast.error(`Failed to update ${config.titleSingle}`)
      }
    },
    [populateHistoryWithUser, practiceTestId, update, navigate, subject, config]
  )

  const handleDelete = useCallback(async () => {
    if (!config) {
      return
    }

    try {
      const { data } = await remove({
        variables: {
          params: practiceTestId,
        },
      })

      if (data?.removePracticeTest) {
        navigate(config.dashboardLink, undefined, {
          searchParams: new URLSearchParams({
            subjectId: String(subject?.id),
            ...(config.dashboardSearchParams ?? {}),
          }),
        })
        toast.success(`${config.titleSingle} deleted successfully`)
      } else {
        toast.error(`Failed to delete ${config.titleSingle}`)
      }
    } catch (error) {
      toast.error(`Failed to delete ${config.titleSingle}`)
    }
  }, [practiceTestId, update, navigate, subject, config])

  useEffect(() => {
    if (!loading && (!practiceTest || !subject || !config)) {
      toast.error(
        `Invalid data provided for editing ${config?.titleSingle ?? 'Test'}, redirecting to ${
          config?.titleMultiple ?? 'Tests'
        } list`
      )
      navigate(config?.dashboardLink ?? cmsRoutes.PRACTICE_TEST_LIST)
    }
  }, [loading, practiceTest, subject, config])

  if (loading || !practiceTest || !subject || loadingHistory || isUsedLoading) {
    return <PageLoader />
  }

  if (!config || config.type !== practiceTest.type) {
    return null
  }

  return (
    <Form
      type={config.type}
      title={config.titleSingle}
      practiceTestOrSubjectId={
        {
          ...practiceTest,
          questions,
        } as PracticeTest
      }
      subject={subject}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isUsedForAdditionalTests={isUsedData?.checkIfPracticeTestUsedInAdditionalTests}
      questionLocationType={config.questionLocationType}
    />
  )
}
